import { useMemo, useState, type ReactNode } from "react";
import { usePostHog } from "posthog-js/react";
import { useParams } from "react-router-dom";

import Button from "components/Button";
import Modal from "components/Modal";
import { formatCurrency } from "helpers/currency";

import { useArrangement } from "./ArrangementContext";
import { QUESTION_ID, isFingerprintPreservationIncluded } from "./helpers";
import KeepsakeSelection from "./KeepsakeSelection";
import type { KeepsakeProduct } from "./types";

const fingerprintProductFilter = (k: KeepsakeProduct) => !!k.fingerprint;

export default function FingerprintSelection() {
  const { caseId } = useParams<{ caseId: string }>();
  const posthog = usePostHog();
  const {
    state,
    config,
    setServiceAnswer,
    clearKeepsakeSelection,
    packageInclusions,
    isReadOnly,
  } = useArrangement();

  const question = config?.service_questions.find(
    (q) => q.id === QUESTION_ID.FINGERPRINTS,
  );
  const answer = state.serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.FINGERPRINTS,
  );
  const yesOption = question?.options?.find((o) => o.id === "yes");
  const noOption = question?.options?.find((o) => o.id === "no");
  const isFpYes = answer?.selectedOptionId === "yes";

  // Same predicate the section heading uses, so the card can't price an
  // offering the heading has just called included.
  const yesIncluded = isFingerprintPreservationIncluded(
    config,
    packageInclusions,
  );

  // Fingerprint jewelry currently in the cart — used to gate the "No thanks"
  // switch on a confirmation modal so customers aren't silently charged for
  // jewelry that requires a preservation they're trying to opt out of.
  const fingerprintItemsInCart = useMemo(() => {
    if (!config) return [];
    return state.selectedKeepsakes
      .map((sel) => {
        const product = config.keepsakes.find((k) => k.id === sel.productId);
        if (!product || !product.fingerprint) return null;
        const variant = product.variants?.find((v) => v.id === sel.variantId);
        const unitPrice = variant?.price ?? product.price;
        return {
          id: product.id,
          name: product.name,
          quantity: sel.quantity,
          lineTotal: unitPrice * sel.quantity,
        };
      })
      .filter(<T,>(x: T | null): x is T => x !== null);
  }, [config, state.selectedKeepsakes]);

  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const commitPreservationAnswer = (selectedOptionId: string) => {
    setServiceAnswer({
      questionId: QUESTION_ID.FINGERPRINTS,
      selectedOptionId,
    });
    posthog?.capture("arrangements_fingerprint_selected", {
      case_id: caseId,
      choice: selectedOptionId,
    });
  };

  const handlePreservationAnswer = (selectedOptionId: string) => {
    // Switching off preservation while fingerprint jewelry sits in the cart
    // would leave the customer paying for pieces that can't be made. Gate the
    // switch on a confirmation that lists exactly what will be removed.
    if (selectedOptionId === "no" && fingerprintItemsInCart.length > 0) {
      setConfirmingRemoval(true);
      return;
    }
    commitPreservationAnswer(selectedOptionId);
  };

  const confirmRemoval = () => {
    for (const item of fingerprintItemsInCart) {
      clearKeepsakeSelection(item.id);
    }
    commitPreservationAnswer("no");
    setConfirmingRemoval(false);
  };

  const trackJewelryChange = (
    product: KeepsakeProduct,
    quantity: number,
    previousQuantity: number,
  ) => {
    posthog?.capture("arrangements_fingerprint_jewelry_crosssell_clicked", {
      case_id: caseId,
      product_id: product.id,
      product_name: product.name,
      direction: quantity > previousQuantity ? "increase" : "decrease",
      quantity,
    });
    // Only fire "added" on the 0 → 1+ transition so the funnel reads as
    // unique-jewelry-attached, not as every modal interaction.
    if (previousQuantity === 0 && quantity > 0) {
      posthog?.capture("arrangements_fingerprint_jewelry_added", {
        case_id: caseId,
        product_id: product.id,
        product_name: product.name,
        product_price: product.price,
        quantity,
      });
    }
  };

  return (
    <div className="space-y-10">
      {question && yesOption && noOption && (
        <div
          role="radiogroup"
          aria-label="Fingerprint preservation"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <DecisionCard
            name="fp-preservation"
            value="yes"
            label="Yes, preserve it"
            tagline="Recommended"
            description={
              <>
                <p>
                  We will capture a fingerprint of your loved one and send the
                  high-resolution image for you to save.
                </p>
                <p className="mt-2">
                  The image can be used for jewelry, a keepsake, or simply to
                  keep close.
                </p>
              </>
            }
            priceLabel={
              yesIncluded ? "Included" : formatCurrency(yesOption.price)
            }
            priceHighlight={yesIncluded}
            isSelected={answer?.selectedOptionId === "yes"}
            disabled={isReadOnly}
            onSelect={() => handlePreservationAnswer("yes")}
          />
          <DecisionCard
            name="fp-preservation"
            value="no"
            label="No thanks"
            description="We won't capture a print. It can't be added later."
            isSelected={answer?.selectedOptionId === "no"}
            disabled={isReadOnly || yesIncluded}
            onSelect={() => handlePreservationAnswer("no")}
          />
        </div>
      )}

      <section className="border-t border-oat pt-10">
        {!isFpYes && (
          <p id="fp-jewelry-gate-hint" className="mb-5 text-sm text-soil">
            Select "Yes, preserve it" above to choose a piece of fingerprint
            jewelry.
          </p>
        )}
        <div aria-describedby={!isFpYes ? "fp-jewelry-gate-hint" : undefined}>
          <KeepsakeSelection
            productFilter={fingerprintProductFilter}
            onSelectionChange={trackJewelryChange}
            disabled={!isFpYes}
          />
        </div>
      </section>

      <Modal
        isOpen={confirmingRemoval}
        onClose={() => setConfirmingRemoval(false)}
        title="Decline fingerprint preservation?"
        titleId="fp-confirm-removal-title"
        descriptionId="fp-confirm-removal-desc"
      >
        <p id="fp-confirm-removal-desc" className="text-soil">
          These pieces need the fingerprint to be made. Changing to "No thanks"
          will remove them from your arrangement.
        </p>

        <ul className="mt-5 divide-y divide-oat rounded-md border border-oat bg-white">
          {fingerprintItemsInCart.map((item) => (
            <li
              key={item.id}
              className="flex items-baseline justify-between gap-4 px-4 py-3"
            >
              <span className="text-sm text-evergreen">
                {item.name}
                <span className="text-soil"> × {item.quantity}</span>
              </span>
              <span className="text-sm font-medium text-evergreen">
                {formatCurrency(item.lineTotal)}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={confirmRemoval} variant="primary" fullWidth>
            Remove and decline fingerprint preservation
          </Button>
          <Button
            onClick={() => setConfirmingRemoval(false)}
            variant="outline"
            fullWidth
          >
            Keep preservation
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function DecisionCard({
  name,
  value,
  label,
  tagline,
  description,
  priceLabel,
  priceHighlight,
  isSelected,
  disabled,
  onSelect,
}: {
  name: string;
  value: string;
  label: string;
  tagline?: string;
  description: ReactNode;
  priceLabel?: string;
  priceHighlight?: boolean;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex w-full items-center rounded-md border p-4 text-left transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-evergreen ${
        disabled ? "cursor-default" : "cursor-pointer"
      } ${
        isSelected
          ? "border-evergreen bg-ivory"
          : `border-olive bg-white ${disabled ? "" : "hover:bg-ivory"}`
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={isSelected}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      <div className="mr-4">
        <div
          className={`h-3 w-3 rounded-full outline outline-2 outline-offset-2 outline-evergreen ${
            isSelected ? "bg-cantelope" : ""
          }`}
        />
      </div>
      <div className="flex-1">
        {tagline && (
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-olive">
            {tagline}
          </p>
        )}
        <div className="flex items-baseline justify-between gap-4">
          <span className="font-bold text-evergreen">{label}</span>
          {priceLabel && (
            <span
              className={
                priceHighlight ? "font-medium text-sage" : "text-evergreen"
              }
            >
              {priceLabel}
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-soil">{description}</div>
      </div>
    </label>
  );
}
